<template>
  <div>
    <h5>Quotes Report</h5>

    <b-card class="mb-4">
      <b-form inline @submit.prevent="fetchReport">
        <ReportPeriodFilter
          :period-type.sync="periodType"
          :time-period.sync="timePeriod"
          :selected-dates.sync="selectedDates"
          :time-options="timeOptions"
          :date-config="fltConfig"
        />
        <button class="btn btn-primary ml-3">Show Report</button>
      </b-form>
    </b-card>

    <LoadingIndicator v-if="loading" centered />
    <b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>
    <span v-else-if="empty">No records found</span>

    <b-card v-else-if="items.length" class="mb-4">
      <b-table striped hover :items="items" :fields="fields">
        <template #cell(userName)="data">
          <b-link :to="{ name: 'UserDetails', params: { id: data.item.userId } }" target="_blank">
            {{ data.value }}
          </b-link>
        </template>
        <template #cell(conversionRatio)="data">{{ data.value }}%</template>
        <template #cell(showDetails)="row">
          <b-btn size="xs" variant="secondary" @click.prevent="toggleDetails(row)">
            {{ row.detailsShowing ? 'Hide' : 'Zoom' }}
          </b-btn>
        </template>
        <template #row-details="row">
          <b-card>
            <LoadingIndicator v-if="row.item.detailsLoading" centered />
            <b-alert v-else-if="row.item.detailsError" show variant="danger">{{ row.item.detailsError }}</b-alert>
            <b-table
              v-else-if="row.item.detailsRows.length"
              hover
              :items="row.item.detailsRows"
              :fields="detailsFields"
              :tbody-tr-class="detailsRowClass"
              class="mb-0 quote-details-table"
            >
              <template #cell(id)="data">
                <b-link :to="leadLink(data.item)" target="_blank" class="record-link">
                  <b-badge :variant="recordVariant(data.item)">
                    {{ data.item.leadTypeId === 3 ? 'Order' : 'Quote' }} #{{ data.value }}
                  </b-badge>
                </b-link>
              </template>
              <template #cell(type)="data">
                <b-badge :variant="data.item.leadTypeId === 3 ? 'success' : 'primary'">{{ data.value }}</b-badge>
              </template>
              <template #cell(status)="data">
                <b-badge :variant="statusVariant(data.value)">{{ data.value || '-' }}</b-badge>
              </template>
              <template #cell(quoteAt)="data">{{ formatDateTime(data.value) }}</template>
              <template #cell(orderAt)="data">{{ formatDateTime(data.value) }}</template>
              <template #cell(signedAt)="data">{{ formatDateTime(data.value) }}</template>
              <template #cell(followupAt)="data">{{ formatDateTime(data.value) }}</template>
              <template #cell(followupStatus)="data">
                <b-badge v-if="data.value" :variant="data.value === 'Expired' ? 'danger' : 'info'">{{ data.value }}</b-badge>
                <span v-else>-</span>
              </template>
            </b-table>
            <span v-else>No records found</span>
          </b-card>
        </template>
        <template #custom-foot="data">
          <b-tr>
            <b-th v-for="field in data.fields" :key="field.key">{{ formatTotal(field.key) }}</b-th>
          </b-tr>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import moment from 'moment';
import { ApiClient } from '@/api/ApiClient';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import ReportPeriodFilter from '@/components/pages/reports/ReportPeriodFilter';

export default {
  name: 'ReportQuotes',
  components: {
    LoadingIndicator,
    ReportPeriodFilter,
  },
  data() {
    return {
      loading: false,
      empty: false,
      error: null,
      items: [],
      totals: {},
      periodType: 'list',
      timePeriod: 1,
      selectedDates: null,
      fltConfig: {
        mode: 'range',
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'm/d/Y',
        allowInput: true,
      },
      timeOptions: [
        { text: 'Current month', value: 1 },
        { text: 'Last month', value: 2 },
        { text: 'Current quarter', value: 3 },
        { text: 'Last quarter', value: 4 },
        { text: 'Current year', value: 5 },
        { text: 'All time', value: 6 },
      ],
      fields: [
        { key: 'userName', label: 'Agent' },
        { key: 'totalCount', label: 'Total', sortable: true },
        { key: 'ordersCount', label: 'Orders', sortable: true },
        { key: 'dispatchedCount', label: 'Dispatched', sortable: true },
        { key: 'canceledCount', label: 'Canceled', sortable: true },
        { key: 'conversionRatio', label: 'Conversion Ratio', sortable: true },
        { key: 'followupsTotal', label: 'Total FU', sortable: true },
        { key: 'followupsExpired', label: 'Total FU Expired', sortable: true },
        { key: 'followupsFuture', label: 'Total FU Future', sortable: true },
        { key: 'showDetails', label: '', sortable: false },
      ],
      detailsFields: [
        { key: 'id', label: 'Quote / Order', sortable: true },
        { key: 'source', label: 'Source', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'quoteAt', label: 'Quoted At', sortable: true },
        { key: 'orderAt', label: 'Ordered At', sortable: true },
        { key: 'signedAt', label: 'Dispatched At', sortable: true },
        { key: 'followupStatus', label: 'Follow-up', sortable: true },
        { key: 'followupAt', label: 'Follow-up At', sortable: true },
      ],
    };
  },
  methods: {
    formatTotal(key) {
      if (key === 'userName') return 'Total';
      if (key === 'showDetails') return '';
      if (key === 'conversionRatio') return `${this.totals[key] || 0}%`;
      return this.totals[key] == null ? '' : this.totals[key];
    },
    toggleDetails(row) {
      row.toggleDetails();
      if (row.item._showDetails && !row.item.detailsLoaded) this.fetchDetails(row.item);
    },
    async fetchDetails(item) {
      const dates = this.prepareDates();
      if (!dates) return;
      this.$set(item, 'detailsLoading', true);
      this.$set(item, 'detailsError', null);
      try {
        const response = await ApiClient.reports.quotesDetails({ params: { ...dates, userId: item.userId } });
        const rows = response && response.data && response.data.data && response.data.data.rows;
        if (!response || response.status >= 400 || !Array.isArray(rows)) {
          this.$set(item, 'detailsError', response && response.data && response.data.message ? response.data.message : 'Unable to load quote details.');
          return;
        }
        this.$set(item, 'detailsRows', rows);
        this.$set(item, 'detailsLoaded', true);
      } catch (error) {
        this.$set(item, 'detailsError', 'Unable to load quote details.');
      } finally {
        this.$set(item, 'detailsLoading', false);
      }
    },
    leadLink(item) {
      if (item.leadTypeId === 3) return { name: 'OrderDetails', params: { id: item.id } };
      if (item.leadTypeId === 2) return { name: 'QuoteDetails', params: { id: item.id } };
      return { name: 'LeadDetails', params: { id: item.id } };
    },
    recordVariant(item) {
      if (item.leadTypeId === 3 && item.status === 'Canceled') return 'danger';
      if (item.leadTypeId === 3) return 'success';
      return 'primary';
    },
    statusVariant(status) {
      if (status === 'Canceled') return 'danger';
      if (['Issues', 'Suspended'].includes(status)) return 'warning';
      if (['Dispatched', 'Completed', 'Picked-Up', 'Delivered'].includes(status)) return 'success';
      if (status === 'New Order') return 'info';
      return 'secondary';
    },
    detailsRowClass(item) {
      if (!item) return '';
      if (item.status === 'Canceled') return 'quote-detail-row-canceled';
      if (item.followupStatus === 'Expired') return 'quote-detail-row-expired';
      if (item.leadTypeId === 3) return 'quote-detail-row-order';
      return 'quote-detail-row-quote';
    },
    formatDateTime(value) {
      if (!value) return '-';
      const date = moment(value);
      return date.isValid() ? date.format('MM/DD/YYYY h:mm A') : value;
    },
    prepareDates() {
      let parts = [];
      if (this.periodType === 'list') {
        if (this.timePeriod === 1) parts = [moment().startOf('month'), moment().endOf('month')];
        if (this.timePeriod === 2) parts = [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')];
        if (this.timePeriod === 3) parts = [moment().startOf('quarter'), moment().endOf('quarter')];
        if (this.timePeriod === 4) parts = [moment().subtract(1, 'quarter').startOf('quarter'), moment().subtract(1, 'quarter').endOf('quarter')];
        if (this.timePeriod === 5) parts = [moment().startOf('year'), moment().endOf('year')];
        if (this.timePeriod === 6) return { createdStart: null, createdEnd: null };
        return {
          createdStart: parts[0].format('YYYY-MM-DD'),
          createdEnd: parts[1].format('YYYY-MM-DD'),
        };
      }

      if (!this.selectedDates) return null;
      const selected = this.selectedDates.split(' to ');
      if (selected.length < 2) selected.push(selected[0]);
      return { createdStart: selected[0], createdEnd: selected[1] };
    },
    async fetchReport() {
      const params = this.prepareDates();
      if (!params) return;

      this.loading = true;
      this.error = null;
      try {
        const response = await ApiClient.reports.quotes({ params });
        const data = response && response.data && response.data.data;

        if (!response || response.status >= 400 || !data) {
          this.items = [];
          this.totals = {};
          this.empty = false;
          this.error = response && response.data && response.data.message
            ? response.data.message
            : 'Unable to load Quotes Report.';
          return;
        }

        const rows = data && Array.isArray(data.rows) ? data.rows : [];
        const totals = data && data.totals ? data.totals : {};
        this.empty = !rows.length;
        this.items = rows.map(row => ({
          ...row,
          detailsRows: [],
          detailsLoading: false,
          detailsLoaded: false,
          detailsError: null,
        }));
        this.totals = totals;
      } catch (error) {
        this.items = [];
        this.totals = {};
        this.empty = false;
        this.error = 'Unable to load Quotes Report.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.record-link:hover {
  text-decoration: none;
}

::v-deep .quote-details-table tbody tr.quote-detail-row-quote > td {
  background-color: rgba(33, 150, 243, 0.08) !important;
}

::v-deep .quote-details-table tbody tr.quote-detail-row-order > td {
  background-color: rgba(40, 167, 69, 0.1) !important;
}

::v-deep .quote-details-table tbody tr.quote-detail-row-expired > td {
  background-color: rgba(255, 193, 7, 0.13) !important;
}

::v-deep .quote-details-table tbody tr.quote-detail-row-canceled > td {
  background-color: rgba(220, 53, 69, 0.16) !important;
}
</style>
